import { useWorkspaceStore } from "@/hooks/useWorkspace";
import React, { useState, useEffect } from "react";
import { API_URL } from "@/env";
import SettingsGeneral from "@/components/Reports/SettingsGeneral";
import SettingsIntegrations from "@/components/Reports/SettingsIntegrations";
import SettingsTeam from "@/components/Reports/SettingsTeam";
import SettingsMCP from "@/components/Reports/SettingsMCP";
import useUserData from "@/hooks/useUserData";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "@/components/Reports/ConfirmModal";
import { AnimatePresence } from "motion/react";
import { getCookie } from "@/lib/cookies";
import { showCustomToast } from "@/components/CustomToast";
import { Workspace } from "@/lib/types";

const Settings = () => {
  const [activeTab, setActiveTab] = useState("General");
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const tabs = ["General", "Team", "Integrations", "MCP"];
  const { currentWorkspace, setCurrentWorkspace, setWorkspaces, workspaces } =
    useWorkspaceStore();
  const { user } = useUserData();
  const navigate = useNavigate();
  const token = getCookie("token");

  const isAdmin =
    currentWorkspace?.users.map((user) => user._id)[0] === user?._id;

  useEffect(() => {
    const fetchWorkspace = async () => {
      try {
        const response = await fetch(
          `${API_URL}/workspaces/${currentWorkspace?._id}`,
        );
        const data = await response.json();
        if (data._id) {
          setCurrentWorkspace(data);
        }
      } catch (err) {
        console.log(err);
      }
    };

    fetchWorkspace();
  }, []);

  useEffect(() => {
    if (
      currentWorkspace &&
      user &&
      !currentWorkspace?.users.map((user) => user._id).includes(user?._id)
    ) {
      navigate("/");
    }
  }, [currentWorkspace, user]);

  const handleLeaveOrDelete = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/workspaces/${currentWorkspace?._id}/leave`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to process request");
      }

      const updatedWorkspaces = workspaces.filter((workspace) => {
        return workspace._id !== currentWorkspace?._id;
      });

      setWorkspaces(updatedWorkspaces);

      const data = await response.json();
      setCurrentWorkspace(user.workspaces[0]);

      showCustomToast(data.message, "check");
      setIsConfirmModalOpen(false);

      // Navigate to home/workspace list
      navigate("/");
    } catch (err) {
      console.error(err);
      showCustomToast(
        err instanceof Error
          ? err.message
          : "An error occurred. Please try again.",
        "exclamation",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="bg-gray-100 max-w-[540px] mx-auto rounded-[24px] relative overflow-scroll no-scrollbar w-full h-[calc(100vh-80px)]">
        <div className="sticky top-0 left-0 border-b bg-gray-100 z-[10] border-gray-300 p-[12px] flex-shrink-0 flex items-center justify-between">
          <nav className="flex overflow-x-auto gap-[2px]">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-1.5 text-[14px] hover:bg-gray-400 font-medium leading-[20px] rounded-[12px] transition-all ${
                  activeTab === tab ? "bg-gray-300" : "bg-transparent"
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
          {workspaces.length !== 1 && (
            <button
              onClick={() => setIsConfirmModalOpen(true)}
              className="hover:bg-red-alpha/[0.08] rounded-[12px] px-3 py-2 text-[14px] transition-all font-medium leading-[20px] text-red-700"
            >
              {isAdmin ? "Delete workspace" : "Leave workspace"}
            </button>
          )}
        </div>
        <div className="w-full p-5">
          {activeTab === "General" && <SettingsGeneral />}
          {activeTab === "Team" && <SettingsTeam />}
          {activeTab === "Integrations" && <SettingsIntegrations />}
          {activeTab === "MCP" && <SettingsMCP />}
        </div>
      </div>

      <AnimatePresence>
        {isConfirmModalOpen && (
          <ConfirmModal
            isAdmin={isAdmin}
            isLoading={isLoading}
            setIsOpen={setIsConfirmModalOpen}
            onConfirm={handleLeaveOrDelete}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Settings;
